import { CheckCircle, Hash, Loader2, Lock, XCircle } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { useMutation } from '@tanstack/react-query';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useGuardContext } from '@/hooks/useGuardContext';
import { useTranslation } from '@/hooks/useTranslation';
import { validatePin } from '@/services/api.client';
import type { PinValidationResult } from '@/types';

const PIN_LENGTH = 6;
const MAX_ATTEMPTS = 5;
// 30-minute lockout after MAX_ATTEMPTS failures — matches the spec (guard-web-app req 8.7)
// and aligns with the server-side throttle window (5 per 10 min, 30-min lockout).
const LOCKOUT_DURATION_MS = 30 * 60 * 1000;

/**
 * PIN Validation Section — 6-digit PIN entry with granted/denied result display.
 * Only rendered when GUARD_WEB_INVITES module is active.
 * Requirements: 8.1–8.5, 8.7
 */
export function PinValidationSection() {
  const { t } = useTranslation();
  const { gateId, isOnline } = useGuardContext();

  const [pin, setPin] = useState('');
  const [result, setResult] = useState<PinValidationResult | null>(null);
  const [actualPartySize, setActualPartySize] = useState<number>(1);
  const [attempts, setAttempts] = useState(0);
  const [lockedUntil, setLockedUntil] = useState<number | null>(null);
  const [lockoutRemaining, setLockoutRemaining] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  const isLocked = lockedUntil !== null && Date.now() < lockedUntil;

  useEffect(() => {
    if (!lockedUntil) return;
    const interval = setInterval(() => {
      const remaining = Math.max(0, Math.ceil((lockedUntil - Date.now()) / 1000));
      setLockoutRemaining(remaining);
      if (remaining <= 0) {
        setLockedUntil(null);
        setAttempts(0);
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [lockedUntil]);

  const validateMutation = useMutation({
    mutationFn: (pinValue: string) => validatePin(pinValue, gateId || undefined),
    onSuccess: (data: PinValidationResult) => {
      setResult(data);
      setActualPartySize(data.partySize ?? 1);
      if (data.result === 'deny') {
        const newAttempts = attempts + 1;
        setAttempts(newAttempts);
        if (newAttempts >= MAX_ATTEMPTS) {
          setLockedUntil(Date.now() + LOCKOUT_DURATION_MS);
        }
      }
    },
    onError: () => {
      const newAttempts = attempts + 1;
      setAttempts(newAttempts);
      if (newAttempts >= MAX_ATTEMPTS) {
        setLockedUntil(Date.now() + LOCKOUT_DURATION_MS);
      }
    },
  });

  const handlePinChange = useCallback(
    (value: string) => {
      const digits = value.replace(/\D/g, '').slice(0, PIN_LENGTH);
      setPin(digits);
      if (digits.length === PIN_LENGTH && !isLocked) {
        validateMutation.mutate(digits);
      }
    },
    [isLocked, validateMutation],
  );

  function handleReset() {
    setPin('');
    setResult(null);
    validateMutation.reset();
    inputRef.current?.focus();
  }

  if (!isOnline) {
    return (
      <div className="rounded-[var(--radius-lg)] border border-[var(--status-amber)] bg-[var(--status-amber-tint)] p-4 text-center">
        <p className="text-sm text-[var(--status-amber-text)]">{t('guard.pin.offlineMessage')}</p>
      </div>
    );
  }

  if (result?.result === 'allow') {
    return (
      <div className="space-y-4 rounded-[var(--radius-lg)] border-2 border-[var(--status-green)] bg-[var(--status-green-tint)] p-6">
        <div className="flex items-center justify-center gap-2">
          <CheckCircle className="size-8 text-[var(--status-green-text)]" />
          <h3 className="text-lg font-semibold text-[var(--status-green-text)]">
            {t('guard.pin.granted')}
          </h3>
        </div>
        <div className="space-y-2 text-center text-sm">
          {result.visitor && (
            <p className="font-medium text-[var(--text-primary)]">{result.visitor.name}</p>
          )}
          {result.host && (
            <p className="text-[var(--text-secondary)]">
              {result.host.name} · {result.host.unit}
            </p>
          )}
          {result.invitationType && (
            <p className="text-xs uppercase tracking-wide text-[var(--text-tertiary)]">
              {result.invitationType.replace(/_/g, ' ')}
            </p>
          )}
          {result.validFrom && result.validUntil && (
            <p className="text-xs text-[var(--text-secondary)]">
              {new Date(result.validFrom).toLocaleTimeString([], {
                hour: '2-digit',
                minute: '2-digit',
              })}
              {' – '}
              {new Date(result.validUntil).toLocaleTimeString([], {
                hour: '2-digit',
                minute: '2-digit',
              })}
            </p>
          )}
          {result.vehicleDetails && (
            <p className="text-xs text-[var(--text-secondary)]">{result.vehicleDetails}</p>
          )}
          {result.partySize && result.partySize > 1 && (
            <div className="mt-2 space-y-1">
              <p className="text-xs font-medium text-[var(--text-primary)]">
                {`${result.partySize} ${t('visitor.partySize.label').toLowerCase()}`}
              </p>
              <div className="flex items-center justify-center gap-2">
                <label htmlFor="actualPartySize" className="text-xs text-[var(--text-secondary)]">
                  {t('visitor.partySize.actual')}:
                </label>
                <Input
                  id="actualPartySize"
                  type="number"
                  min={1}
                  value={actualPartySize}
                  onChange={(e) => setActualPartySize(Math.max(1, parseInt(e.target.value) || 1))}
                  className="w-16 min-h-[36px] text-center"
                />
              </div>
            </div>
          )}
        </div>
        <div className="flex gap-3">
          <Button variant="outline" className="min-h-[44px] flex-1" onClick={handleReset}>
            {t('common.cancel')}
          </Button>
          <Button
            className="min-h-[44px] flex-1 bg-[var(--status-green)] hover:bg-[var(--status-green)]/90"
            onClick={handleReset}
          >
            {t('guard.pin.allowEntry')}
          </Button>
        </div>
      </div>
    );
  }

  if (result?.result === 'deny') {
    return (
      <div className="space-y-4 rounded-[var(--radius-lg)] border-2 border-[var(--status-red)] bg-[var(--status-red-tint)] p-6">
        <div className="flex items-center justify-center gap-2">
          <XCircle className="size-8 text-[var(--status-red-text)]" />
          <h3 className="text-lg font-semibold text-[var(--status-red-text)]">
            {t('guard.pin.denied')}
          </h3>
        </div>
        {result.reason && (
          <p className="text-center text-sm text-[var(--text-primary)]">{result.reason}</p>
        )}
        {result.guardMessage && (
          <p className="text-center text-xs text-[var(--text-secondary)]">{result.guardMessage}</p>
        )}
        <Button variant="outline" className="min-h-[44px] w-full" onClick={handleReset}>
          {t('guard.pin.tryAgain')}
        </Button>
      </div>
    );
  }

  if (isLocked) {
    const minutes = Math.floor(lockoutRemaining / 60);
    const seconds = lockoutRemaining % 60;
    return (
      <div className="space-y-3 rounded-[var(--radius-lg)] border border-[var(--status-amber)] bg-[var(--status-amber-tint)] p-6 text-center">
        <Lock className="mx-auto size-8 text-[var(--status-amber-text)]" />
        <h3 className="text-sm font-semibold text-[var(--status-amber-text)]">
          {t('guard.pin.locked')}
        </h3>
        <p className="font-mono text-lg font-semibold text-[var(--text-primary)] tabular-nums">
          {String(minutes).padStart(2, '0')}:{String(seconds).padStart(2, '0')}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3 rounded-[var(--radius-lg)] border border-[var(--border-default)] bg-[var(--bg-surface)] p-4">
      <div className="flex items-center gap-2">
        <Hash className="size-5 text-[var(--text-accent)]" />
        <h3 className="text-sm font-semibold text-[var(--text-primary)]">{t('guard.pin.title')}</h3>
      </div>
      <div className="space-y-2">
        <Label htmlFor="pin-input" className="sr-only">
          {t('guard.pin.label')}
        </Label>
        <div className="relative">
          <Input
            ref={inputRef}
            id="pin-input"
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={PIN_LENGTH}
            value={pin}
            onChange={(e) => handlePinChange(e.target.value)}
            placeholder={t('guard.pin.placeholder')}
            className="min-h-[44px] text-center font-mono text-lg tracking-[0.5em]"
            autoComplete="off"
            disabled={validateMutation.isPending}
          />
          {validateMutation.isPending && (
            <Loader2 className="absolute right-3 top-1/2 size-4 -translate-y-1/2 animate-spin text-[var(--text-secondary)]" />
          )}
        </div>
      </div>
      {validateMutation.error && (
        <p className="text-xs text-[var(--text-error)]" role="alert">
          {(validateMutation.error as { message?: string })?.message ?? t('common.error')}
        </p>
      )}
    </div>
  );
}
